import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Facebook, Instagram, CreditCard, Truck, Banknote } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);

const Footer = () => {
    return (
        <footer className="bg-gradient-to-b from-amber-950 to-amber-900 text-white relative z-20">
            {/* Main Footer */}
            <div className="container-main py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Brand */}
                    <div className="lg:col-span-1">
                        <h3 className="text-2xl font-bold mb-5">
                            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Вкусът на</span>{' '}
                            <span className="text-white">Турция</span>
                        </h3>
                        <p className="text-amber-200/70 mb-6 leading-relaxed">
                            Вашият доверен партньор за автентични турски деликатеси и премиум продукти за дома.
                        </p>
                        <div className="flex gap-3">
                            <a 
                                href="https://www.facebook.com/profile.php?id=61588073983274" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-800/50 hover:bg-blue-600 transition-colors"
                                aria-label="Facebook"
                            >
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a 
                                href="https://www.instagram.com/vkusatnaturciq/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-800/50 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 transition-colors"
                                aria-label="Instagram"
                            >
                                <Instagram className="h-5 w-5" />
                            </a>
                            <a 
                                href="https://www.tiktok.com/@diyanova?_r=1&_t=ZN-94nJYfaUqs2" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-800/50 hover:bg-black transition-colors"
                                aria-label="TikTok"
                            >
                                <TikTokIcon className="h-5 w-5" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-semibold text-lg mb-6 text-white">Навигация</h4>
                        <ul className="space-y-4">
                            {[
                                { name: 'Продукти', path: '/products' },
                                { name: 'Контакти', path: '/contact' }
                            ].map((link) => (
                                <li key={link.path}>
                                    <Link to={link.path} className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal & Payment */}
                    <div>
                        <h4 className="font-semibold text-lg mb-6 text-white">Информация</h4>
                        <ul className="space-y-4">
                            <li>
                                <Link to="/faq" className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                    Често задавани въпроси
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                    Общи условия
                                </Link>
                            </li>
                            <li>
                                <Link to="/returns" className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                    Връщане и възстановяване
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy" className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                    Поверителност
                                </Link>
                            </li>
                        </ul>
                        
                        {/* Payment Methods */}
                        <div className="mt-8">
                            <p className="text-amber-300/50 text-sm mb-3">Методи на плащане</p>
                            <div className="flex items-center gap-3">
                                <div className="bg-amber-800/50 p-2.5 rounded-lg" title="Карта">
                                    <CreditCard className="h-5 w-5 text-amber-300" />
                                </div>
                                <div className="bg-amber-800/50 p-2.5 rounded-lg" title="Наложен платеж">
                                    <Truck className="h-5 w-5 text-amber-300" />
                                </div>
                                <div className="bg-amber-800/50 p-2.5 rounded-lg" title="Банков превод">
                                    <Banknote className="h-5 w-5 text-amber-300" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="font-semibold text-lg mb-6 text-white">Контакти</h4>
                        <ul className="space-y-5">
                            <li className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-amber-800/50 flex items-center justify-center flex-shrink-0">
                                    <MapPin className="h-5 w-5 text-amber-400" />
                                </div>
                                <div>
                                    <p className="text-amber-200/70">
                                        гр. София<br />
                                        ул. Пиротска 85
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-amber-800/50 flex items-center justify-center flex-shrink-0">
                                    <Phone className="h-5 w-5 text-amber-400" />
                                </div>
                                <a href="tel:0899972727" className="text-amber-200/70 hover:text-amber-400 transition-colors font-medium text-lg">
                                    0899 97 27 27
                                </a>
                            </li>
                            <li className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-amber-800/50 flex items-center justify-center flex-shrink-0">
                                    <Mail className="h-5 w-5 text-amber-400" />
                                </div>
                                <a href="mailto:vkustnaturciq39@gmail.com" className="text-amber-200/70 hover:text-amber-400 transition-colors">
                                    vkustnaturciq39@gmail.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-amber-800/50">
                <div className="container-main py-6">
                    {/* Company Legal Info */}
                    <div className="mb-4 p-4 bg-amber-900/50 rounded-lg text-center">
                        <p className="text-amber-200/80 text-sm font-medium mb-1">
                            ВКУСЪТ НА ТУРЦИЯ ЕООД (VKUSAT NA TURKEY EOOD)
                        </p>
                        <p className="text-amber-200/60 text-xs">
                            ЕИК: 208822640 • ДДС №: BG208822640 • МОЛ: Димитър Александров Веселинов
                        </p>
                        <p className="text-amber-200/60 text-xs">
                            Адрес на управление: гр. София, ул. Осогово 60 • Тел: 0899 97 27 27
                        </p>
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-amber-300/50 text-sm">
                            © {new Date().getFullYear()} Вкусът на Турция. Всички права запазени.
                        </p>
                        <p className="text-amber-300/50 text-sm">
                            Цени в EUR с включен ДДС • Доставка с Еконт • 14 дни право на връщане
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
